import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * Mates Ramos: la galería solo se reconstruye cuando cambian de verdad sus
   * imágenes. Quien lo decide es data-mr-gallery-key, que Liquid calcula con
   * las variantes que aportan fotos y el color: si la clave que llega es la
   * misma, no se toca el DOM, así cambiar el grabado no provoca parpadeo ni
   * pierde la imagen que el usuario estaba mirando. Si falta la clave, se
   * sustituye como hacía el tema.
   *
   * El color solo se mira para decidir si merece la pena enseñar el estado de
   * carga: es el caso en el que la galería cambia entera.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Estado de carga discreto mientras llega la nueva galería
    if (this.#colorChanged(event.target)) this.dataset.mrLoading = 'true';

    event.promise
      .then(({ detail }) => {
        delete this.dataset.mrLoading;

        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const currentKey = this.dataset.mrGalleryKey;
        const newKey = /** @type {HTMLElement} */ (newMediaGallery).dataset.mrGalleryKey;
        if (currentKey && newKey && currentKey === newKey) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        // Si el fetch falla se mantiene la galería actual, sin romper la página
        delete this.dataset.mrLoading;
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * ¿Ha cambiado el color respecto al que pintó esta galería?
   *
   * Lee el selector de variantes en vez del payload del evento: cuando llega
   * el evento, el input ya está marcado, y así no dependemos de la forma
   * exacta del evento estándar de Shopify.
   *
   * @param {Element} picker - El elemento que disparó el evento (variant-picker).
   * @returns {boolean | null} true si cambió, false si es el mismo color,
   *   null si no se puede saber (entonces se sustituye como hacía el tema).
   */
  #colorChanged(picker) {
    const optionName = this.dataset.mrColorOption;
    const currentValue = this.dataset.mrColorValue;
    if (!optionName || !currentValue) return null;

    const selector = `[data-option-name="${CSS.escape(optionName)}"]`;
    const checkedRadio = picker.querySelector(`input[type="radio"]${selector}:checked`);
    const selectedOption = Array.from(picker.querySelectorAll(`option${selector}`)).find(
      (option) => /** @type {HTMLOptionElement} */ (option).selected
    );

    const nextValue = /** @type {HTMLInputElement | HTMLOptionElement | null} */ (checkedRadio ?? selectedOption)
      ?.value;
    if (nextValue == null) return null;

    return nextValue !== currentValue;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
